<script setup lang="ts">
import { useMarketCartStore } from '@/stores/cart.store';
import { formatAmount } from '@/utils/formatters';
import { inject, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import SpinnerQuantityInput from './components/SpinnerQuantityInput.vue';
import MarketCartInfos from './components/MarketCartInfos.vue';
import { showAlert } from '@/utils/swal';
import ConfirmationModal from '@/components/ConfirmationModal.vue';

const BACKEND_URL = inject('BACKEND_URL');
const router = useRouter();
const layout = ref('none');
const cart = useMarketCartStore();
const { items } = storeToRefs(cart);
const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
const loadingRemoveFromCart = ref(false);
const errorRemoveFromCart = ref('');

onMounted(() => {
    layout.value = router.currentRoute.value.meta.layout as string || 'none';
});

function onItemQuantityChanged(item_id: number, new_quantity: number) {
    cart.setQuantity(item_id, new_quantity);
}

async function onRemoveItemFromCart(item_id: number) {
    const confirm = await confirmationModalRef.value?.open("Panier", "Voulez-vous vraiment retirer cet article du panier ?");
    if (confirm) {
        try {
            loadingRemoveFromCart.value = true;
            cart.removeFromCart(item_id);
        } catch(error: any) {
            errorRemoveFromCart.value = error.message || 'Une erreur est survenue lors de la requete au serveur. Veuillez réessayer.';
            await showAlert.error(errorRemoveFromCart.value);
        } finally {
            loadingRemoveFromCart.value = false;
        }
    }
}
</script>
<template>
    <div class="page-space-50" v-if="layout == 'none'"></div>
    <section class="container-lil">
            <h1 class="secondary-font mb-6">Panier / {{ cart.getTotalItems }} Articles</h1>

            <div style="height: 200px; display: flex; justify-content: center; align-items: center;" v-if="items.length == 0">
                <h4 class="secondary-font">Aucun article ajouté dans le panier</h4>
            </div>

            <template v-if="items.length > 0">
                <div class="s10_mobile">
                    <!---->
                    <div class="flex-center mb-6" v-for="(item, index) of items" :key="index">
                        <div class="s5">
                            <a href="">
                                <div class="img-area mb-0 px-3 py-2">
                                    <img :src="BACKEND_URL + '/assets/market_produits/300/' + item.image" width="100%" />
                                </div>
                            </a>
                        </div>
                        <div class="s1">&nbsp;</div>
                        <div class="s14">
                            <a href="">
                                <h6 class="title secondary-font text-grey-600 mb-3">{{ item.nom }}</h6>
                            </a>
                            <h5 class="price secondary-font mb-3">{{ item.quantite + ' x ' + formatAmount(item.montant_apres_remises) }} <span class="secondary-font">F</span></h5>
                            <div class="flex align-items-center">
                                <spinner-quantity-input :model-value="item.quantite" :min-value="1" @update:model-value="onItemQuantityChanged(item.id, $event)"></spinner-quantity-input>
                                <div style="flex: 1"><p class="font-semibold secondary-font text-medium text-grey-600 ml-5" v-if="item.nb_echeance > 0">Payer en {{ item.nb_echeance }} fois</p></div>
                                <button class="btn sm light" title="Retirer du panier" @click="onRemoveItemFromCart(item.id)"><img src="/public/icons/icon-code-delete.svg" width="16px" /></button>
                            </div>
                        </div>
                    </div>
                    <!---->
                </div>

                <div class="s1_mobile">&nbsp;</div>

                <div class="s9_mobile">
                    <market-cart-infos :layout="layout"></market-cart-infos>
                </div>
            </template>
    </section>

    <div class="clear-both h-60"></div>
    <confirmation-modal ref="confirmationModalRef"></confirmation-modal>
</template>
